import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const heroSlides = [
  {
    image: "https://images.unsplash.com/photo-1761499102697-17f54b78fba2",
    title: "Votre projet clé en main",
    subtitle: "Zéro stress, on s'occupe de tout de A à Z"
  },
  {
    image: "https://images.unsplash.com/photo-1644589104114-41ea93fc02e7",
    title: "Excellence et savoir-faire",
    subtitle: "15 ans d'expérience au service de votre intérieur"
  },
  {
    image: "https://images.pexels.com/photos/6585753/pexels-photo-6585753.jpeg",
    title: "De l'idée à la réalité",
    subtitle: "Concrétisez vos rêves de rénovation avec nos experts"
  },
  {
    image: "https://images.unsplash.com/photo-1761053133165-0f3acdaf1770",
    title: "Rénovation sans compromis",
    subtitle: "Qualité premium et respect du patrimoine parisien"
  }
];

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <section id="accueil" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Slides */}
      {heroSlides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F3C]/95 via-[#0B1F3C]/85 to-[#0B1F3C]/70"></div>
          </div>
        </div>
      ))}

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10 pt-32 pb-20">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6 animate-fade-in">
            <span className="text-[#c9a961] text-sm font-medium">✨ Spécialiste de la rénovation parisienne</span>
          </div>

          {/* Main Heading - Changes with slides */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in">
            {heroSlides[currentSlide].title.split(' ').map((word, i) => (
              <span key={i}>
                {word === 'clé' || word === 'Excellence' || word === 'réalité' || word === 'compromis' ? (
                  <span className="text-[#c9a961]">{word}</span>
                ) : (
                  word
                )}{' '}
              </span>
            ))}
          </h1>

          {/* Subheading - Changes with slides */}
          <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed animate-fade-in">
            {heroSlides[currentSlide].subtitle}
          </p>

          {/* Features List */}
          <div className="flex flex-col gap-3 mb-10">
            <div className="flex items-center gap-3 text-white/90">
              <CheckCircle className="text-[#c9a961] flex-shrink-0" size={20} />
              <span className="text-lg">Devis gratuit et sans engagement</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <CheckCircle className="text-[#c9a961] flex-shrink-0" size={20} />
              <span className="text-lg">Intervention dans toute l'Île-de-France</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <CheckCircle className="text-[#c9a961] flex-shrink-0" size={20} />
              <span className="text-lg">Artisans qualifiés et expérimentés</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              className="bg-[#c9a961] hover:bg-[#b89951] text-white font-semibold px-8 py-6 text-lg group"
            >
              Demander un devis
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
            <Button
              onClick={() => scrollToSection('realisations')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#0B1F3C] font-semibold px-8 py-6 text-lg"
            >
              Voir nos réalisations
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 group"
        aria-label="Précédent"
      >
        <ChevronLeft className="text-white group-hover:scale-110 transition-transform" size={24} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 group"
        aria-label="Suivant"
      >
        <ChevronRight className="text-white group-hover:scale-110 transition-transform" size={24} />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? 'bg-[#c9a961] w-8'
                : 'bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`Aller à la slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Decorative Element */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent z-10"></div>
    </section>
  );
};

export default Hero;
